import { EnvironmentInjector, inject, Injectable } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  ClientServiceCreateResponseJson,
  ClientServiceCreateResponseSchema,
  ClientServiceListRequestSchema,
  CollectionNames,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, filter, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { ClientRepositoryService, withSearchSync } from '../../repositories';
import { VOID } from '../../shared/utils';
import * as ClientsActions from './clients.actions';

@Injectable()
export class ClientsEffects extends AuthRunEffects {
  private readonly repository = inject(ClientRepositoryService);
  private readonly injector = inject(EnvironmentInjector);
  private readonly errorHandler = inject(GlobalErrorHandlerService);

  listClients$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientsActions.listClients),
      concatLatestFrom(() => of(true)), //this.store.select(selectAuthLoading)),
      filter(([, loading]) => loading), // see https://github.com/eslint/eslint/issues/7838#issuecomment-269982977
      withSearchSync(CollectionNames.Clients, this.injector),
      switchMap(([payload]) =>
        this.repository
          .list(
            fromJson(ClientServiceListRequestSchema, {
              pageSize: 5000,
              ...payload.params,
            }),
          )
          .pipe(
            map((result) =>
              ClientsActions.loadClients({
                clients: (result.items ?? []).map(
                  (item) =>
                    toJson(
                      ClientServiceCreateResponseSchema,
                      item,
                      defaultApiSerializeJsonOptions,
                    ) as ClientServiceCreateResponseJson,
                ),
              }),
            ),
            catchError((error) => {
              this.errorHandler.handleError(error);
              return of(ClientsActions.listClientsFailure(error));
            }),
          ),
      ),
    );
  });

  loadClients$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientsActions.loadClients),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      concatMap(() => EMPTY as Observable<{ type: string }>),
    );
  });

  deleteClient$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ClientsActions.deleteClient),
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        switchMap(({ id }) => this.repository.delete(id)),
        catchError((error) => {
          this.errorHandler.handleError(error);
          return VOID;
        }),
      );
    },
    { dispatch: false },
  );

  loadSingleClient$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientsActions.loadSingleClient),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      switchMap(({ uuid }) => this.repository.fetch(uuid)),
      map((client) =>
        ClientsActions.addClient({
          client: toJson(
            ClientServiceCreateResponseSchema,
            client,
            defaultApiSerializeJsonOptions,
          ) as ClientServiceCreateResponseJson,
        }),
      ),
      catchError((error) => {
        this.errorHandler.handleError(error);
        return of(ClientsActions.loadSingleClientFailure({ error }));
      }),
    );
  });

  constructor() {
    super(inject(Actions));
  }
}
